import {
  ComponentFactoryResolver,
  Directive,
  ElementRef,
  Injector,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Renderer2
} from '@angular/core';
import { Overlay } from '@angular/cdk/overlay';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

import { TooltipDirective } from '../truncate-tooltip/tooltip.directive';
import { ICON_BUTTON_TOOLTIPS, IconButtonEnum } from './icon-button.enum';

/**
 * Marks an icon-only button (or clickable icon) and shows its standard tooltip.
 * The text comes from ICON_BUTTON_TOOLTIPS by the button type; rnplIconButtonTooltip overrides it (already translated text).
 * An empty type and no override — no tooltip.
 *
 * <button class="btn btn-icon" [rnplIconButton]="iconButtonEnum.DELETE">…</button>
 * <button class="btn btn-icon" [rnplIconButton]="iconButtonEnum.EDIT" [rnplIconButtonTooltip]="'BUTTON.EDIT_NAME' | translate">…</button>
 */
@Directive({
  selector: '[rnplIconButton]'
})
export class IconButtonDirective extends TooltipDirective implements OnChanges, OnDestroy {

  // IconButtonEnum value; templates may pass it as a string literal, e.g. rnplIconButton="delete"
  @Input('rnplIconButton') iconButton: IconButtonEnum | string;
  @Input('rnplIconButtonTooltip') tooltip: string;

  private translationSubscription: Subscription = null;

  constructor(
    elementRef: ElementRef<HTMLElement>,
    overlay: Overlay,
    ngZone: NgZone,
    renderer: Renderer2,
    injector: Injector,
    componentFactoryResolver: ComponentFactoryResolver,
    private readonly translateService: TranslateService,
  ) {
    super(elementRef, overlay, ngZone, renderer, injector, componentFactoryResolver);
  }

  ngOnChanges(): void {
    this.unsubscribeTranslation();

    if (this.tooltip) {
      this.setText(this.tooltip);
      return;
    }

    const key = this.iconButton ? ICON_BUTTON_TOOLTIPS[this.iconButton as IconButtonEnum] : null;
    if (!key) {
      this.setText(null);
      return;
    }

    this.translationSubscription = this.translateService.stream(key)
      .subscribe((text: string) => this.setText(text));
  }

  ngOnDestroy(): void {
    this.unsubscribeTranslation();
    super.ngOnDestroy();
  }

  private setText(text: string): void {
    this.text = text;
    super.ngOnChanges();
  }

  private unsubscribeTranslation(): void {
    if (this.translationSubscription) {
      this.translationSubscription.unsubscribe();
      this.translationSubscription = null;
    }
  }
}
